import { Controller } from "@hotwired/stimulus"
import InlineFootnotes from "../lib/inline_footnotes"

const COLUMN_GAP = 40
const NOTE_GAP = 16
const MIN_WIDTH = 160
const MAX_WIDTH = 320
const EDGE_GAP = 16
const ACCEPTABLE_SHIFT = 200
const MIN_TOC_ITEMS = 2

const sublistOf = item => item.querySelector(":scope > ol") || item.appendChild(document.createElement("ol"))

// 元の脚注をそのまま配置する。ID・参照リンク・戻るリンクと印刷時のリストを維持する。
export default class extends Controller {
  connect() {
    this.connected = true
    this.notes = []
    this.inlineFootnotes = new InlineFootnotes(this.element)
    this.observedElements = new Set()
    // Turbo のキャッシュから復元された場合は、保存済みの空スペーサーと目次を再利用する。
    this.spacer = this.element.querySelector(":scope > .margin-notes-spacer") || document.createElement("div")
    this.spacer.className = "margin-notes-spacer"
    this.spacer.setAttribute("aria-hidden", "true")
    this.toc = this.element.querySelector(":scope > .margin-toc") || document.createElement("nav")
    this.toc.className = "margin-toc"
    this.toc.setAttribute("aria-label", "目次")
    // 復元した目次を、置き直すまで前の画面幅の位置に出さない。
    this.toc.hidden = true
    this.element.prepend(this.toc)
    this.element.append(this.spacer)
    this.resizeObserver = new ResizeObserver(this.scheduleLayout)
    this.mutationObserver = new MutationObserver(records => {
      // 自分で開閉した欄の挿入・削除・内容更新と目次の書き換えを、本文の差し替えとして再処理しない。
      const changed = records.some(record => !record.target.parentElement?.closest(".inline-footnote, .margin-toc")
        && !record.target.closest?.(".inline-footnote, .margin-toc")
        && !(record.type === "childList" && [...record.addedNodes, ...record.removedNodes]
          .every(node => node.nodeType === 1 && node.matches(".inline-footnote"))))
      if (changed) this.scheduleLayout()
    })
    this.mutationObserver.observe(this.element, { childList: true, subtree: true, characterData: true })
    window.addEventListener("resize", this.scheduleLayout)
    this.element.addEventListener("load", this.scheduleLayout, true)
    this.element.addEventListener("toggle", this.scheduleLayout, true)
    document.addEventListener("turbo:before-cache", this.reset)
    document.fonts.ready.then(this.scheduleLayout)
    this.scheduleLayout()
  }

  disconnect() {
    this.connected = false
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()
    window.removeEventListener("resize", this.scheduleLayout)
    this.element.removeEventListener("load", this.scheduleLayout, true)
    this.element.removeEventListener("toggle", this.scheduleLayout, true)
    document.removeEventListener("turbo:before-cache", this.reset)
    cancelAnimationFrame(this.pending)
    this.reset()
    this.inlineFootnotes.disconnect()
    this.spacer.remove()
    this.toc.remove()
  }

  scheduleLayout = () => {
    if (!this.connected || !this.element.isConnected) return
    cancelAnimationFrame(this.pending)
    this.pending = requestAnimationFrame(() => this.layout())
  }

  reset = () => {
    this.resetLayout()
    this.inlineFootnotes.disable()
  }

  resetLayout() {
    for (const { note, footnotes } of this.notes) {
      note.classList.remove("margin-note")
      footnotes.classList.remove("margin-notes-footnotes")
      delete note.dataset.marginNoteNumber
      delete note.dataset.marginNoteSide
      for (const property of ["--margin-note-x", "--margin-note-y", "--margin-note-width"]) {
        note.style.removeProperty(property)
      }
    }
    this.spacer.style.height = "0px"
  }

  place(element, column, y) {
    const origin = element.offsetParent.getBoundingClientRect()
    element.style.setProperty("--margin-note-width", `${column.width}px`)
    element.style.setProperty("--margin-note-x", `${column.x - origin.left - element.offsetParent.clientLeft}px`)
    element.style.setProperty("--margin-note-y", `${y - origin.top - element.offsetParent.clientTop}px`)
    element.dataset.marginNoteSide = column.side
    return y + element.getBoundingClientRect().height + NOTE_GAP
  }

  placeToc(columns) {
    const rightColumn = columns.find(column => column.side === "right")
    const headings = rightColumn ? this.collectHeadings() : []
    this.toc.hidden = headings.length < MIN_TOC_ITEMS
    // 印刷時は CSS が隠し、配置の基準になる offsetParent を持たない。
    if (!this.toc.offsetParent) return

    this.renderToc(headings)
    rightColumn.bottom = this.place(this.toc, rightColumn, this.element.querySelector(".page").getBoundingClientRect().top)
  }

  collectHeadings() {
    // 脚注と同じく、個別に Markdown 化した領域は同じ見出し ID を持ち得るので、最初の見出しだけを載せる。
    const ids = new Set()
    return [...this.element.querySelectorAll(".page :is(h1, h2, h3, h4, h5, h6) > span.fragment[id]")].flatMap(anchor => {
      const heading = anchor.parentElement
      if (ids.has(anchor.id)) return []
      ids.add(anchor.id)
      return [{ id: anchor.id, level: Number(heading.tagName.slice(1)), text: heading.textContent.trim() }]
    })
  }

  renderToc(headings) {
    const list = document.createElement("ol")
    const stack = []
    for (const { id, level, text } of headings) {
      while (stack.length && stack.at(-1).level >= level) stack.pop()
      const parentList = stack.length ? sublistOf(stack.at(-1).item) : list
      const item = document.createElement("li")
      const link = document.createElement("a")
      link.href = `#${encodeURIComponent(id)}`
      link.textContent = text
      item.append(link)
      parentList.append(item)
      stack.push({ level, item })
    }
    // 内容が同じなら差し替えず、目次のリンクに置いたフォーカスを保つ。
    if (list.isEqualNode(this.toc.lastElementChild)) return

    const title = document.createElement("p")
    title.className = "margin-toc-title"
    title.setAttribute("aria-hidden", "true")
    title.textContent = "目次"
    this.toc.replaceChildren(title, list)
  }

  collectNotes() {
    return [...this.element.querySelectorAll(".page .footnotes")].flatMap(footnotes => {
      // summary / details / 基本情報は個別に Markdown 化され、同じ脚注 ID を持ち得る。
      const references = [...footnotes.parentElement.querySelectorAll("a.footnote")]
        .filter(reference => !reference.closest(".inline-footnote"))
      return [...footnotes.querySelectorAll(":scope > ol > li[id]")].flatMap(note => {
        const noteReferences = references.filter(link => link.getAttribute("href") === `#${note.id}`)
        const reference = noteReferences.find(link => link.getClientRects().length)
        return reference ? [{ note, reference, references: noteReferences, footnotes }] : []
      })
    })
  }

  layout() {
    this.resetLayout()
    this.notes = this.collectNotes()
    // 監視対象を更新する。自身の位置調整は最終サイズが同じなら再通知されない。
    const observedElements = new Set([this.element, ...this.element.querySelectorAll(".page"), ...this.notes.map(({ note }) => note)])
    for (const element of this.observedElements) {
      if (!observedElements.has(element)) this.resizeObserver.unobserve(element)
    }
    for (const element of observedElements) {
      if (!this.observedElements.has(element)) this.resizeObserver.observe(element)
    }
    this.observedElements = observedElements

    const bounds = this.element.getBoundingClientRect()
    const viewportWidth = document.documentElement.clientWidth
    const columns = [
      { side: "right", x: bounds.right + COLUMN_GAP, width: Math.min(MAX_WIDTH, viewportWidth - bounds.right - COLUMN_GAP - EDGE_GAP), bottom: bounds.top },
      { side: "left", width: Math.min(MAX_WIDTH, bounds.left - COLUMN_GAP - EDGE_GAP), bottom: bounds.top },
    ].filter(column => column.width >= MIN_WIDTH)
    for (const column of columns) {
      if (column.side === "left") column.x = bounds.left - COLUMN_GAP - column.width
    }
    if (!columns.length) {
      this.toc.hidden = true
      this.inlineFootnotes.update(this.notes)
      return
    }
    this.inlineFootnotes.disable()

    for (const { note, reference, footnotes } of this.notes) {
      footnotes.classList.add("margin-notes-footnotes")
      note.classList.add("margin-note")
      note.dataset.marginNoteNumber = reference.textContent.trim()
    }
    // 脚注一覧がフローから外れた後の位置で並べる。ページが縮むとスクロール位置も変わりうる。複数の Markdown 領域も一緒に扱う。
    this.placeToc(columns)
    const ordered = this.notes.map(entry => ({ ...entry, top: entry.reference.getBoundingClientRect().top }))
      .sort((a, b) => a.top - b.top)

    for (const { note, top } of ordered) {
      // 右を優先し、右で大きく下がる場合だけ左を使う。両方で大きく下がる場合は移動量が少ない列へ下げる。
      const column = columns.find(candidate => candidate.bottom - top <= ACCEPTABLE_SHIFT)
        || columns.reduce((best, candidate) => candidate.bottom < best.bottom ? candidate : best)
      column.bottom = this.place(note, column, Math.max(top, column.bottom))
    }

    // 長い脚注や目次が本文を越えても、下のフッターに重ならないだけの高さを残す。
    const bottom = Math.max(...columns.map(column => column.bottom))
    this.spacer.style.height = `${Math.max(0, Math.ceil(bottom - this.spacer.getBoundingClientRect().top))}px`
  }
}
